import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, Unauthenticated, type Me, type Store } from '../api.js';
import { operations } from '../liveops/api.js';
import { brainVersions, type Versions } from '../version/api.js';
import { useViewing, viewFetch, viewingRevision, type Viewing } from '../view-as.js';

/** C3 is live. Retain the isolated legacy provider behind this switch for one release. */
export const USE_ACCESS_API = true;

/** C1, including work and connector keys that the legacy menu cannot grant. */
export type FeatureKey =
  | 'page.triage' | 'page.tasks' | 'page.chats' | 'page.meetings' | 'page.meetings.team' | 'page.meetings.company'
  | 'page.calendar' | 'page.companies' | 'page.people' | 'page.deals' | 'page.momentum.team' | 'page.momentum.own'
  | 'page.matters' | 'page.contracts' | 'page.rights' | 'page.playbooks' | 'page.intel.company' | 'page.intel.industry'
  | 'page.operations' | 'page.usage' | 'page.engineering' | 'page.policy'
  | 'work.intel_readout' | 'work.copilot' | 'work.copilot_approver' | 'work.call_summaries' | 'work.summary_mail'
  | 'work.summary_mail_every_call' | 'work.record_teams_calls' | 'work.lost_call_card' | 'work.ops_clock_cards'
  | 'work.counted_on_momentum' | 'work.deal_proposals' | 'work.personal_store'
  | 'connector.snowflake' | 'connector.anaplan_all' | 'connector.anaplan_commercial' | 'edit.records';

/** The C3 wire answer. Keep this in step with OX7; pages consume it through the provider. */
export interface Access {
  store: { tenantId: string; kind: 'commercial' | 'personal' };
  person: { principalId: string; displayName: string; group: string | null; role: 'member' | 'admin' | 'super_admin'; keeper: boolean };
  features: string[];
  processing: { historyMonths: number; historyWhole: boolean; quickTriage: boolean };
  version: { label: string; picker: boolean };
  viewing: null | { sessionId: string; principalId: string; displayName: string; group: string | null };
}

export const EVERYONE_FEATURES: readonly FeatureKey[] = [
  'page.triage', 'page.tasks', 'page.chats', 'page.meetings', 'page.calendar', 'page.companies', 'page.people',
  'page.deals', 'page.matters', 'page.contracts', 'page.rights', 'page.playbooks', 'page.intel.company',
  'page.intel.industry', 'page.policy',
];

/** Only the sources today's menu/picker already use. Undecidable tab keys follow their page’s menu gate; unknown work and keeper powers stay denied. */
export function legacyAccess({ me, store, view, operations = false, momentum = false, versions }: {
  me: Me; store: Store; view?: Viewing | undefined; operations?: boolean; momentum?: boolean; versions?: Versions | null;
}): Access {
  const role = view?.role ?? store.role;
  const features: FeatureKey[] = [...EVERYONE_FEATURES, 'page.meetings.team', 'page.meetings.company'];
  if (role === 'super_admin') features.push('page.usage', 'page.engineering');
  if (operations) features.push('page.operations');
  // Today's menu does not distinguish Meetings or Momentum tabs. Their keys answer menu visibility only
  // until C3 arrives; the page's existing API still decides which figures it returns.
  if (momentum) features.push('page.momentum.team', 'page.momentum.own');
  const label = versions?.mine ?? '';
  return {
    store: { tenantId: store.tenantId, kind: store.kind },
    person: { principalId: view?.principalId ?? store.principalId, displayName: view?.displayName ?? me.login.displayName,
      group: view?.group ?? null, role: role === 'super_admin' || role === 'admin' ? role : 'member', keeper: false },
    features,
    processing: { historyMonths: 2, historyWhole: false, quickTriage: false },
    version: { label, picker: !view && !!label && !!versions?.versions?.length },
    viewing: view ? { sessionId: view.sessionId, principalId: view.principalId, displayName: view.displayName, group: view.group } : null,
  };
}

/** api.ts does not export its JSON/get helper. Use its error types and the same view-aware transport here. */
export async function readAccess(store: string): Promise<Access> {
  const res = await viewFetch(`/api/me/access?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' });
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: unknown };
    throw new ApiError(res.status, typeof body.error === 'string' ? body.error : `${res.status} ${res.statusText}`);
  }
  return await res.json() as Access;
}

const AccessContext = createContext<Access | null>(null);
type ProviderProps = { me: Me; store: Store; children: ReactNode };
let epoch = -1;
const answers = new Map<string, Promise<Access>>();
const versions = new Map<string, Promise<Versions | null>>();

function accessKey(me: Me, store: Store): string {
  return JSON.stringify([viewingRevision(), me.login.id, store.tenantId, store.principalId, store.role, store.kind]);
}
function clearOldReader(): void {
  if (epoch !== viewingRevision()) { answers.clear(); versions.clear(); epoch = viewingRevision(); }
}

/** Separate components mean enabling C3 never mounts the legacy hooks or makes their requests. */
export function AccessProvider(props: ProviderProps) {
  return USE_ACCESS_API ? <ServerAccessProvider {...props} /> : <LegacyAccessProvider {...props} />;
}

let asked: Promise<boolean> | null = null;
let operationsEpoch = -1;

function operationsAccess(): Promise<boolean> {
  if (operationsEpoch !== viewingRevision()) { asked = null; operationsEpoch = viewingRevision(); }
  asked ??= operations.access().catch(() => false);
  return asked;
}

/** False until the answer comes back, so an entry the reader may not open is never drawn even for a frame. */
function useLegacyOperationsAccess(): boolean {
  useViewing();
  const revision = viewingRevision();
  const [answer, setAnswer] = useState<{ revision: number; allowed: boolean } | null>(null);
  useEffect(() => {
    let live = true;
    void operationsAccess().then((allowed) => { if (live) setAnswer({ revision, allowed }); });
    return () => { live = false; };
  }, [revision]);
  return answer?.revision === revision && answer.allowed;
}

let momentumEpoch = -1;
const momentumAnswers = new Map<string, Promise<boolean>>();

/** Shared with the kit provider: one answer per store and effective reader. */
function momentumAccess(store: string): Promise<boolean> {
  if (momentumEpoch !== viewingRevision()) { momentumAnswers.clear(); momentumEpoch = viewingRevision(); }
  let answer = momentumAnswers.get(store);
  if (!answer) {
    answer = viewFetch(`/api/momentum/access?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then(async r => r.ok && (await r.json() as { allowed: boolean }).allowed === true, () => false)
      .catch(() => false);
    momentumAnswers.set(store, answer);
  }
  return answer;
}

function useLegacyMomentumAccess(store: string): boolean {
  useViewing();
  const key = JSON.stringify([store, viewingRevision()]);
  const [answer, setAnswer] = useState<{ key: string; allowed: boolean } | null>(null);
  useEffect(() => {
    let live = true;
    void momentumAccess(store).then(allowed => { if (live) setAnswer({ key, allowed }); });
    return () => { live = false; };
  }, [key, store]);
  return answer?.key === key && answer.allowed;
}

function LegacyAccessProvider({ me, store, children }: ProviderProps) {
  const view = useViewing();
  const operations = useLegacyOperationsAccess();
  const momentum = useLegacyMomentumAccess(store.tenantId);
  const key = accessKey(me, store);
  const [loaded, setLoaded] = useState<{ key: string; versions: Versions | null } | null>(null);
  useEffect(() => {
    clearOldReader();
    let request = versions.get(key);
    if (!request) {
      request = brainVersions.list(store.tenantId).catch(() => null);
      versions.set(key, request);
    }
    let live = true;
    void request.then(versions => { if (live) setLoaded({ key, versions }); });
    return () => { live = false; };
  }, [key, store.tenantId]);
  const access = legacyAccess({ me, store, view, operations, momentum, versions: loaded?.key === key ? loaded.versions : null });
  return <AccessContext.Provider value={access}>{children}</AccessContext.Provider>;
}

/** One C3 answer per store and effective reader. Pages use AccessProvider. */
export function ServerAccessProvider({ me, store, children }: ProviderProps) {
  useViewing();
  const key = accessKey(me, store);
  const [loaded, setLoaded] = useState<{ key: string; access: Access } | null>(null);
  useEffect(() => {
    clearOldReader();
    let request = answers.get(key);
    if (!request) { request = readAccess(store.tenantId); answers.set(key, request); }
    let live = true;
    void request.then(access => { if (live) setLoaded({ key, access }); }, () => { if (live) setLoaded(null); });
    return () => { live = false; };
  }, [key, store.tenantId]);
  // A render after a store/view switch never exposes the old answer while the effect catches up.
  const access = loaded?.key === key ? loaded.access : null;
  return <AccessContext.Provider value={access}>{children}</AccessContext.Provider>;
}

/** C3 returns null while reading/on failure; legacy uses today's immediate everyone-pages and default-deny hooks. */
export function useAccess(): Access | null { return useContext(AccessContext); }
export function useFeature(key: FeatureKey): boolean { return useAccess()?.features?.includes(key) ?? false; }

/** Administration is the only role gate. The answer already names the effective person under a view. */
export function useIsAdmin(): boolean {
  const role = useAccess()?.person.role;
  return role === 'admin' || role === 'super_admin';
}

/** Compatibility imports for one release; neither hook makes an independent request. */
export function useOperationsAccess(): boolean { return useFeature('page.operations'); }
export function useMomentumAccess(_store: string): boolean {
  const team = useFeature('page.momentum.team');
  const own = useFeature('page.momentum.own');
  return team || own;
}
